import novaOpsApi from '../../lib/novaOpsApi';

/**
 * Client contract for the Nova customer graph (apps/api ops/customers/graph,
 * reached through the Strata proxy at /nova-ops/ops/customers/graph).
 */
export type CustomerGraphKind = 'identity' | 'account' | 'app_user' | 'wallet';
export type CustomerGraphHop = 0 | 1 | 2;

export interface GraphIdentity {
  entityUuid: string;
  idType: string | null;
  idNumber: string | null;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  identityAssuranceLevel: string | null;
  ricaStatus: string | null;
  ficaStatus: string | null;
  hop: CustomerGraphHop;
}

export interface GraphAccount {
  accountUuid: string;
  iCustomer: number | null;
  msisdn: string | null;
  status: string | null;
  iccid: string | null;
  simStatus: string | null;
  ricaEntityUuid: string | null;
  attributedPartnerName: string | null;
  hop: CustomerGraphHop;
}

export interface GraphAppUser {
  appUserUuid: string;
  username: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  status: string | null;
  recoveryPhoneNumber: string | null;
  ficaTier: string | null;
  ficaEntityUuid: string | null;
  lastLoginAt: string | null;
  hop: CustomerGraphHop;
}

export interface GraphWallet {
  walletUuid: string;
  walletAccountNumber: string;
  name: string | null;
  status: string | null;
  ownerAppUserUuid: string;
  hop: CustomerGraphHop;
}

export type GraphEdge =
  | { kind: 'RICA_ENTITY'; accountUuid: string; entityUuid: string }
  | { kind: 'FICA_ENTITY'; appUserUuid: string; entityUuid: string }
  | {
      kind: 'APP_USER_ACCOUNT';
      appUserUuid: string;
      accountUuid: string;
      isPrimary: boolean;
      verified: boolean;
      nickname: string | null;
    }
  | { kind: 'WALLET_OWNER'; walletUuid: string; appUserUuid: string };

export type GraphWarning =
  | {
      kind: 'IDENTITY_MISMATCH';
      accountUuid: string;
      appUserUuid: string;
      ricaEntityUuid: string;
      ficaEntityUuid: string;
    }
  | { kind: 'NO_FICA_IDENTITY'; appUserUuid: string }
  | { kind: 'TRUNCATED'; nodeKind: CustomerGraphKind; limit: number };

export interface CustomerGraph {
  tenant: { mvno: string; displayName?: string; database?: string };
  focus: { kind: CustomerGraphKind; id: string };
  identities: GraphIdentity[];
  accounts: GraphAccount[];
  appUsers: GraphAppUser[];
  wallets: GraphWallet[];
  edges: GraphEdge[];
  warnings: GraphWarning[];
}

export interface CustomerGraphResponse {
  success: boolean;
  kind: CustomerGraphKind;
  graphs: CustomerGraph[];
}

export async function fetchCustomerGraph(
  kind: CustomerGraphKind,
  value: string,
  mvno?: string | null,
): Promise<CustomerGraphResponse> {
  const params = new URLSearchParams({ kind, value: value.trim() });
  if (mvno) params.set('mvno', mvno);
  const { data } = await novaOpsApi.get<CustomerGraphResponse>(
    `/ops/customers/graph?${params.toString()}`,
  );
  return data;
}

/** Provider wallet account numbers are ten digits starting with 3 or 7. */
export const WALLET_ACCOUNT_NUMBER_PATTERN = /^[37]\d{9}$/;

export const isWalletAccountNumber = (value: string) =>
  WALLET_ACCOUNT_NUMBER_PATTERN.test(value.replace(/\s+/g, ''));

/** Accounts linked to an app user, with the link's primary/verified flags. */
export function accountsLinkedToAppUser(graph: CustomerGraph, appUserUuid: string) {
  return graph.edges.flatMap((edge) => {
    if (edge.kind !== 'APP_USER_ACCOUNT' || edge.appUserUuid !== appUserUuid) return [];
    const account = graph.accounts.find((candidate) => candidate.accountUuid === edge.accountUuid);
    return account ? [{ account, isPrimary: edge.isPrimary, verified: edge.verified }] : [];
  });
}
